import 'react-datepicker/dist/react-datepicker.css';

import { Box, Flex, FormControl, FormErrorMessage, FormLabel } from '@chakra-ui/react';
import { forwardRef, MouseEventHandler, ReactNode } from 'react';
import DatePicker from 'react-datepicker';
import { GoChevronDown } from 'react-icons/go';

import Calendar from 'components/Icons/Calendar';
import { accent, font, neutral, radius, text as textToken } from 'styles/revampTokens';

import '../../PlanPageUIRevamp/datePickerTheme.css';

interface Properties {
  value?: ReactNode;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  planPermission?: boolean;
}

// The control draws its own field rather than sitting inside one the parent
// draws: it needs hover and focus states, and a wrapper cannot give it those.
//
// Icon, label and caret are composed by hand instead of through Chakra's
// leftIcon/rightIcon — those are flex children like the label, so any
// justification spreads all three and the date ends up marooned in the middle.
// The label takes the free space, which seats it beside the icon and pins the
// caret right, matching the select above it.
//
// Chrome stays greyscale at rest and only picks up the accent on hover/focus,
// per the revamp's rule that purple marks interaction, not structure.
const DatePickerInput = forwardRef<HTMLButtonElement, Properties>(
  ({ value, onClick }, reference) => (
    <Box
      as='button'
      type='button'
      // Cast: Box types its ref/handler as HTMLDivElement, but react-datepicker
      // hands a button ref down to its customInput.
      ref={reference as any}
      onClick={onClick as any}
      // Box, not Button: Chakra's `unstyled` Button variant sets `padding: 0`,
      // which beat the px prop and left the icon flush against the border.
      // A Box carries no variant styles to fight.
      display='flex'
      alignItems='center'
      gap='10px'
      width='100%'
      height='43px'
      px='14px'
      bg={neutral.surface}
      border={`1px solid ${neutral.border}`}
      borderRadius={radius.control}
      cursor='pointer'
      textAlign='left'
      fontFamily={font.body}
      fontWeight={500}
      fontSize='13px'
      color={textToken.primary}
      transition='border-color 0.15s ease, box-shadow 0.15s ease'
      _hover={{ borderColor: accent.solid }}
      _focusVisible={{
        outline: 'none',
        borderColor: accent.solid,
        boxShadow: `0 0 0 3px ${accent.subtle}`,
      }}
    >
      <Box as='span' display='flex' alignItems='center' flexShrink={0}>
        <Calendar color={textToken.secondary} />
      </Box>
      <Box
        as='span'
        flex='1'
        minW={0}
        overflow='hidden'
        whiteSpace='nowrap'
        textOverflow='ellipsis'
      >
        {value}
      </Box>
      <Box as='span' display='flex' alignItems='center' flexShrink={0}>
        <GoChevronDown size={16} color={textToken.muted} />
      </Box>
    </Box>
  ),
);
const ScenarioDatePicker = ({
  selected,
  onChange,
  placeholder,
  label,
  errorMessage,
  timescale,
  onSelect,
  clearDateInput,
  dateId,
  startIn,
  endIn,
  deleteStart,
  clearSection = true,
  isDisabled,
}: any) => {
  return (
    <FormControl width='100%' height='100%' data-testid='datePicker-test'>
      {label && <FormLabel>{label}</FormLabel>}

      <DatePicker
        data-testid='datePicker-test'
        // The shared `datePickerStyle` sx this used to carry painted the
        // calendar cyan. The revamp theme class is the same one the reporting
        // period pickers use, so every calendar in the revamp now matches.
        popperClassName='dashboard-date-picker-theme'
        popperPlacement='bottom-start'
        selected={selected}
        onChange={onChange}
        disabled={isDisabled}
        placeholderText={placeholder}
        dateFormat={timescale === 'Y' ? 'yyyy' : 'MMM yyyy'}
        showMonthYearPicker={timescale === 'M'}
        showYearPicker={timescale === 'Y'}
        customInput={<DatePickerInput />}
        onSelect={onSelect}
      >
        {clearSection && (
          <Flex
            marginBottom='15px'
            borderTop='1px solid rgba(112, 138, 138, 0.3)'
            width='90%'
            display='flex'
            justifyContent='center'
            alignItems='center'
            paddingTop='10px'
            color='purple'
            onClick={() => {
              clearDateInput(dateId, deleteStart, startIn, endIn);
            }}
          >
            <Box>Clear Selection</Box>
          </Flex>
        )}
      </DatePicker>

      <FormErrorMessage>{errorMessage}</FormErrorMessage>
    </FormControl>
  );
};
export default ScenarioDatePicker;
